import React, { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import b from 'b_';

import { GenerationOptions, MultiplePdfGeneratorLink } from '@edu-frontend/modules/src/features/pdfGenerator';
import { BypassTagCheck } from '@edu-frontend/modules/src/shared/lib/BypassTagCheck/BypassTagCheck';
import { Button, CERTIFICATE_LAYOUT } from 'platform-components';
import { CertificateInfo } from 'platform-components/src/components/awards/utils';

import { buildFallbalckCertFilename, useOnPdfGenerationError } from 'utils/download-file';
import { objectMap } from '@edu-frontend/modules/src/shared/lib/object';

interface Props {
    certificates: Record<number, CertificateInfo>;
    courseId: number;
}

export function GenerateCertsZipButton({ certificates, courseId, children }: PropsWithChildren<Props>) {
    const intl = useIntl();

    const [isGenerating, setIsGenerating] = useState(false);

    const onPdfGenerationError = useOnPdfGenerationError();
    const getGenerationOptions = useCallback(() => {
        const res: { [filename: string]: GenerationOptions } = {};

        const templatesCache: { [template in string]?: ReturnType<GenerationOptions['getTemplate']> } = {};

        for (const [studentId, { template, args }] of Object.entries(certificates)) {
            if (template) {
                let filename = buildFallbalckCertFilename(intl, studentId, args, '.pdf');

                if (res.hasOwnProperty(filename)) {
                    filename = filename.replace(/\.\w+$/, (m) => ` [${studentId}]${m}`);
                }

                res[filename] = {
                    getTemplate() {
                        return (templatesCache[template!] ||= fetch(template!).then((resp) => resp.json()));
                    },
                    getInputs() {
                        return [args!];
                    },
                };
            }
        }

        return res;
    }, [intl, certificates]);
    const onGenerationFailure = useCallback(
        (err: unknown) =>
            onPdfGenerationError('pdfGeneration.failure.archive', err, {
                certificates: objectMap(certificates, (x) => x.template),
                courseId,
            }),
        [onPdfGenerationError, certificates, courseId]
    );
    const onGenerationFinally = useCallback(() => setIsGenerating(false), []);
    const onGenerationInitiated = useCallback(() => setIsGenerating(true), []);

    const deps = useMemo(
        () => [
            courseId,
            Object.entries(certificates)
                .map(([studentId, { template }]) => `${studentId}:${template}`)
                .sort()
                .join(),
        ],
        [certificates, courseId]
    );

    return (
        /* eslint-disable react/jsx-sort-props */
        <BypassTagCheck
            _of_={Button}
            as={MultiplePdfGeneratorLink}
            // button props
            className={b('certificate-banner', 'button', { layout: CERTIFICATE_LAYOUT.WIDE })}
            progress={isGenerating}
            size={Button.SIZE.M}
            transparencyTheme={Button.TRANSPARENCY_THEME.DARK}
            view={Button.VIEW.PSEUDO}
            // generation props
            defaultLooseInputs={true}
            deps={deps}
            download={intl
                .formatMessage({ id: 'specCourse.lessonView.studentsCertificates.archive.fileName' })
                .replace(/(\.zip)?$/, '.zip')}
            getGenerationOptions={getGenerationOptions}
            onGenerationFailure={onGenerationFailure}
            onGenerationFinally={onGenerationFinally}
            onGenerationInitiated={onGenerationInitiated}
            preloadFonts={true}
        >
            {children}
        </BypassTagCheck>
        /* eslint-enable react/jsx-sort-props */
    );
}
